import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useAccountsQuery } from './useAccountsQuery';
import { useAccountsSearch } from './useAccountsSearch';
import { Account } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is OPTIONAL on Account (types.ts), which is not the library's
// preferred shape for a default key — a nullable key degrades to insertion
// order if EVERY row lacks it. The alternatives are worse: `id` and `sub` are
// opaque, and sorting operators by an opaque id means nothing to an admin.
//
// `created` is still the right key. It is immutable (unlike `updated`, which
// would reorder the list under someone reading it), and listAccounts already
// returns created-descending, so the first paint AGREES with the server rather
// than reshuffling it. Rows missing it sort last, which is correct.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Account> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useAccounts = (filters: Record<string, string> = {}) => {
    const query = useAccountsQuery(filters);
    const { keyword } = useAccountsSearch();

    // Type guard rather than a bare flatMap: an errored or undefined page in
    // `pages` would otherwise throw on `.items`.
    const items: Account[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen, so the footer states a LOADED count
    // rather than a total, and `unfilteredCount` is reported alongside the
    // match count so the admin can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.email?.toLowerCase().includes(needle) ||
        item.username?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE admin is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<Account>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
